/**
 * 系统预设头像 API
 *
 * 两个端点：
 * - GET  /api/public/avatars?type=xxx —— 公共只读，未登录也可取（注册页要展示），
 *   返回按分类筛选后的目录，URL 由后端读取时现签。
 * - POST /api/user/settings/avatar/select —— 需登录，只接受 code，
 *   由后端按 code 查目录得到 object key 后落库。
 *
 * httpClient 拦截器已把 {code,data,message} 解包为业务数据，这里不要再解包。
 */
import { httpGet, httpPost } from '@/utils/httpClient'
import { wrapResponse, type ApiResponse } from '@/services/types'
import type { AvatarCategoryKey, SystemAvatar } from '@/types/system-avatar'

const LIST_PATH = '/api/public/avatars'
const SELECT_PATH = '/api/user/settings/avatar/select'

/** 后端 SystemAvatarItem 的原始形态，可空字段在此收口为前端必填 */
interface BackendSystemAvatarItem {
  id: string
  code: string
  category: string
  name: string | null
  url: string | null
  url2x: string | null
  thumbnailUrl: string | null
  fileName: string | null
  fileSize: number | null
  storagePath: string
  isDefault: boolean | null
}

function adaptAvatar(raw: BackendSystemAvatarItem): SystemAvatar {
  return {
    id: raw.id,
    code: raw.code,
    category: raw.category as AvatarCategoryKey,
    name: raw.name ?? raw.code,
    url: raw.url ?? '',
    url2x: raw.url2x ?? raw.url ?? '',
    thumbnailUrl: raw.thumbnailUrl ?? raw.url ?? '',
    fileName: raw.fileName ?? `${raw.code}.png`,
    fileSize: raw.fileSize ?? 0,
    storagePath: raw.storagePath,
    isDefault: raw.isDefault ?? false,
  }
}

export const systemAvatarsService = {
  /** 拉取预设头像列表；type 省略时返回全部（默认位在最前）。失败时抛出，由调用方提示。 */
  async listAvatars(type?: AvatarCategoryKey): Promise<SystemAvatar[]> {
    const data = await httpGet<BackendSystemAvatarItem[]>(
      LIST_PATH,
      type ? { params: { type } } : undefined
    )
    return (data ?? []).map(adaptAvatar)
  },

  /** 选用预设头像：只提交 code，后端据此查目录取 object key 落库 */
  async selectPresetAvatar(code: string): Promise<ApiResponse<{ avatarUrl: string }>> {
    const data = await httpPost<{ avatarUrl: string }>(SELECT_PATH, { code })
    return wrapResponse(data)
  },
}
